// WorkerManager.ts - Optimized Web Worker manager
import type { OHLCData, TimeFrame } from "../types";

interface PendingRequest {
  resolve: (data: OHLCData[]) => void;
  reject: (error: Error) => void;
}

class AggregationWorkerManager {
  private worker: Worker | null = null;
  private pendingRequests = new Map<string, PendingRequest>();
  private requestId = 0;

  // Determine if worker should be used.
  //
  // Previously the worker was only used for very large datasets (>=1000) or
  // for 1w/1mn. In practice the on-scroll backward-load path operates on
  // smaller slices (a few hundred bars) and was running aggregation on the
  // main thread, blocking pan/zoom. The structured-clone cost for a few
  // hundred bars is much smaller than the aggregation work itself, so we
  // now use the worker for any non-1m timeframe once the data exceeds a low
  // threshold.
  private static readonly WORKER_MIN_BARS = 200;

  private shouldUseWorker(dataLength: number, timeframe: TimeFrame): boolean {
    if (timeframe === "1m") return false; // No aggregation needed
    if (dataLength < AggregationWorkerManager.WORKER_MIN_BARS) return false;
    return true;
  }

  // Initialize worker lazily
  private initWorker(): void {
    if (this.worker) return;

    try {
      this.worker = new Worker(
        new URL('./aggregation.worker.ts', import.meta.url),
        { type: 'module' }
      );

      this.worker.onmessage = (event) => {
        const { id, result, error } = event.data;
        const request = this.pendingRequests.get(id);
        
        if (request) {
          this.pendingRequests.delete(id);
          
          if (error) {
            request.reject(new Error(error));
          } else {
            request.resolve(result);
          }
        }
      };

      this.worker.onerror = (error) => {
        console.error('[AggregationWorker] Error:', error);
        // Reject all pending requests
        this.pendingRequests.forEach(request => {
          request.reject(new Error('Worker error'));
        });
        this.pendingRequests.clear();
      };

    } catch (error) {
      console.error('[AggregationWorker] Failed to initialize:', error);
      this.worker = null;
    }
  }

  // Main aggregation method
  async aggregate(data: OHLCData[], timeframe: TimeFrame): Promise<OHLCData[]> {
    // Fast path: no aggregation needed
    if (timeframe === "1m" || data.length === 0) {
      return data;
    }

    // Decide between worker and main thread
    if (!this.shouldUseWorker(data.length, timeframe)) {
      return this.aggregateMainThread(data, timeframe);
    }

    // Use worker for heavy aggregations
    return this.aggregateWorker(data, timeframe);
  }

  // Worker-based aggregation
  private async aggregateWorker(data: OHLCData[], timeframe: TimeFrame): Promise<OHLCData[]> {
    this.initWorker();
    
    if (!this.worker) {
      // Fallback to main thread if worker failed
      return this.aggregateMainThread(data, timeframe);
    }

    const id = `req_${++this.requestId}`;

    return new Promise((resolve, reject) => {
      this.pendingRequests.set(id, { resolve, reject });

      this.worker!.postMessage({
        id,
        type: 'aggregate',
        data,
        timeframe
      });

      // Timeout fallback
      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id);
          console.warn('[AggregationWorker] Timeout, falling back to main thread');
          this.aggregateMainThread(data, timeframe).then(resolve).catch(reject);
        }
      }, 5000);
    });
  }

  // Main thread fallback (simplified version of original)
  private aggregateMainThread(data: OHLCData[], timeframe: TimeFrame): Promise<OHLCData[]> {
    return new Promise((resolve) => {
      // Import and use original aggregation function
      import('../stores/aggregation').then(({ aggregateOHLCData }) => {
        resolve(aggregateOHLCData(data, timeframe));
      });
    });
  }

  // Cleanup
  destroy(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.pendingRequests.clear();
  }
}

// Singleton instance
const aggregationWorkerManager = new AggregationWorkerManager();

export { aggregationWorkerManager };
